/**
 * ehr#741 (Chase 10-30; PROGRESS-NOTE V141 V100): the editor's side of the review cards'
 * conversations (body.reviewThreads): a reply typed on a card, a tick (keep / resolve), and which
 * comment threads show beside a section. Plain functions so they can be tested without the editor;
 * the server stamps new replies (lib reviewThreads in @ehr/shared).
 */
import type { NoteSuggestions, ReviewThread } from '@ehr/shared';
import { commentThreadId } from '@ehr/shared';

export type { ReviewThread };

/**
 * ehr#741 (Chase 10-31, point 6): the threads with a card closed by the red X left out under the
 * given id, so a new card on that field starts a new thread instead of the old one.
 */
export function withoutClosed(threads: readonly ReviewThread[], id: string): ReviewThread[] {
  return threads.filter((x) => !(x.id === id && x.closed));
}

/** A reply typed on a card: added with an empty `at` (not saved yet) and the editor's own id. */
export function withReply(
  threads: readonly ReviewThread[],
  t: { id: string; section: string },
  me: { by: string; round: number },
  text: string,
  rid: string,
): ReviewThread[] {
  threads = withoutClosed(threads, t.id);
  const words = text.trim().slice(0, 2000);
  if (!words) return [...threads];
  const reply = { id: rid, by: me.by, at: '', text: words };
  const has = threads.some((x) => x.id === t.id);
  if (!has)
    return [
      ...threads,
      { id: t.id, section: t.section, by: me.by, at: '', round: me.round, replies: [reply] },
    ];
  return threads.map((x) => (x.id === t.id ? { ...x, replies: [...x.replies, reply] } : x));
}

/** The green check: ticks a card (resolved), or takes the tick off. */
export function withTick(
  threads: readonly ReviewThread[],
  t: { id: string; section: string },
  me: { by: string; round: number },
): ReviewThread[] {
  threads = withoutClosed(threads, t.id);
  const has = threads.find((x) => x.id === t.id);
  if (!has)
    return [
      ...threads,
      {
        id: t.id,
        section: t.section,
        by: me.by,
        at: '',
        round: me.round,
        replies: [],
        resolved: true,
      },
    ];
  return threads.map((x) => {
    if (x.id !== t.id) return x;
    const { resolved, ...rest } = x;
    return resolved ? rest : { ...rest, resolved: true };
  });
}

/**
 * The comment threads beside a section: those kept on the note (each comment sent back, with its
 * replies), else, for a note sent back before they were kept, the comments of its suggestions.
 * `open` leaves out the resolved ones.
 */
export function commentThreadsFor(
  threads: readonly ReviewThread[],
  suggestions: NoteSuggestions | null,
  section: string,
): ReviewThread[] {
  const kept = threads.filter((t) => t.id.startsWith('c:'));
  if (kept.length) return kept.filter((t) => t.section === section);
  if (!suggestions) return [];
  return suggestions.comments
    .map((c, n) => ({
      id: commentThreadId(suggestions.round, n),
      section: c.section,
      text: c.text,
      ...(c.quote ? { quote: c.quote, ...(c.pos !== undefined ? { pos: c.pos } : {}) } : {}),
      by: suggestions.by,
      at: c.at ?? suggestions.at,
      round: suggestions.round,
      replies: [],
    }))
    .filter((t) => t.section === section);
}

/**
 * The reviewer's draft as it is saved (and compared, so nothing is saved while it is unchanged):
 * their copy of the boxes, the comments posted, their changes to the other fields, and the cards'
 * threads with any replies or ticks not sent yet. `empty` when there is nothing of theirs in it.
 */
export function draftToSave(o: {
  sections: Record<string, string>;
  comments: {
    id: string;
    section: string;
    text: string;
    quote?: string;
    pos?: number;
    at: string;
  }[];
  fields?: Record<string, unknown>;
  threads: readonly ReviewThread[];
  storedThreads: readonly ReviewThread[];
}) {
  const threadsMoved = JSON.stringify(o.threads) !== JSON.stringify(o.storedThreads);
  const empty = !Object.keys(o.sections).length && !o.comments.length && !o.fields && !threadsMoved;
  return {
    empty,
    draft: {
      sections: o.sections,
      comments: o.comments,
      ...(o.fields ? { fields: o.fields } : {}),
      ...(threadsMoved ? { threads: [...o.threads] } : {}),
    },
  };
}
